import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { authApi } from '../api/authApi';
import { Spinner } from '@/shared/ui/Spinner';
import { Button } from '@/shared/ui/Button';
import styles from './VerifyPage.module.css';

export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid verification link');
      return;
    }

    const verify = async () => {
      try {
        const response = await authApi.verifyToken(token);
        const { access_token, refresh_token, user } = response.data;

        // flushSync ensures auth state is committed before navigation,
        // so ProtectedRoute sees isAuthenticated=true on first render
        flushSync(() => {
          login(access_token, refresh_token, user);
        });

        if (user.role === 'owner' && !user.practiceId) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verify();
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <div className={styles.center}>
        <div className={styles.heroIcon}>
          <XCircle size={48} strokeWidth={1.5} aria-hidden />
        </div>
        <h2 className={styles.title}>Verification failed</h2>
        <p className={styles.message}>{error}</p>
        <Button variant="primary" onClick={() => navigate('/login')}>
          Back to login
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.center}>
      <Spinner size="lg" />
      <p className={styles.loading}>Verifying your link...</p>
    </div>
  );
}
